import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import JSZip from "jszip";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, FileText, Download, Archive, Loader2 } from "lucide-react";
import ReportSheet, { gradeBadgeClass } from "../components/ReportSheet";

const pdfOpts = (filename) => ({
  margin: 0, filename,
  image: { type: "jpeg", quality: 0.98 },
  html2canvas: { scale: 2, useCORS: true },
  jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  pagebreak: { mode: ["css", "legacy"] },
});

export default function ClassReports() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/classes/${classId}/reports`).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Gagal memuat"));
  }, [classId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 text-muted-foreground">Memuat...</div>;
  const c = data.class;
  const meta = { cls: c, academic: data.academic_components, nonAcademic: data.non_academic_components, scale: data.grading_scale, iecLogo: data.iec_logo, title: data.report_title, program: data.program_name, orgAddress: data.org_address, bmName: data.bm_name, bmSignature: data.bm_signature };
  const fileFor = (name) => `Progress-Report-${(name || "siswa").replace(/\s+/g, "-")}-${(c.school_name || "").replace(/\s+/g, "-")}-Sem-${c.semester}-${c.academic_year}.pdf`;

  const downloadCombined = async () => {
    if (!data.reports.length) return;
    setBusy("combined");
    try {
      const el = document.getElementById("pdf-pool");
      await html2pdf().set(pdfOpts(`Rapor-Kelas-${(c.name || "kelas").replace(/\s+/g, "-")}-Sem-${c.semester}.pdf`)).from(el).save();
    } catch (e) { toast.error("Gagal membuat PDF"); }
    setBusy("");
  };

  const downloadZip = async () => {
    if (!data.reports.length) return;
    setBusy("zip");
    try {
      const zip = new JSZip();
      for (const r of data.reports) {
        const el = document.getElementById(`sheet-${r.student.id}`);
        // eslint-disable-next-line no-await-in-loop
        const blob = await html2pdf().set(pdfOpts(fileFor(r.student.name))).from(el).outputPdf("blob");
        zip.file(fileFor(r.student.name), blob);
      }
      const content = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(content);
      a.download = `Rapor-Kelas-${(c.name || "kelas").replace(/\s+/g, "-")}-Sem-${c.semester}.zip`;
      a.click();
    } catch (e) { toast.error("Gagal membuat ZIP"); }
    setBusy("");
  };

  return (
    <div className="space-y-4" data-testid="class-reports-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate("/classes")} className="rounded-full gap-1" data-testid="reports-back"><ArrowLeft className="w-4 h-4" /> Kembali</Button>
        <div className="flex gap-2">
          <Button onClick={downloadCombined} disabled={!!busy || !data.reports.length} variant="outline" className="rounded-full gap-1" data-testid="download-combined-btn">{busy === "combined" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Gabungan PDF</Button>
          <Button onClick={downloadZip} disabled={!!busy || !data.reports.length} className="rounded-full bg-gtsnavy text-white gap-1" data-testid="download-zip-btn">{busy === "zip" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />} Download ZIP</Button>
        </div>
      </div>

      <Card className="rounded-2xl border-none shadow-sm p-5 bg-white">
        <h2 className="font-nunito font-extrabold text-xl text-gtsnavy">Student Progress Report — {c.name}</h2>
        <p className="text-sm text-muted-foreground">{c.school_name} · Guru {c.teacher_name} · Sem {c.semester} · {c.academic_year}</p>
      </Card>

      {data.reports.length === 0 ? (
        <Card className="rounded-2xl border-dashed p-10 text-center text-muted-foreground" data-testid="reports-empty">Belum ada siswa di kelas ini.</Card>
      ) : (
        <Card className="rounded-2xl border-none shadow-sm bg-white divide-y divide-slate-100">
          {data.reports.map((r) => (
            <div key={r.student.id} className="flex items-center justify-between px-4 py-3" data-testid={`report-item-${r.student.id}`}>
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-gtsnavy/10 text-gtsnavy text-xs flex items-center justify-center font-bold">{r.student.number}</span>
                <div>
                  <p className="font-medium text-gtsnavy">{r.student.name}</p>
                  <p className="text-xs text-muted-foreground">Angka {r.scores.average ?? "-"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`rounded-full ${gradeBadgeClass(r.scores.letter)}`} data-testid={`report-grade-${r.student.id}`}>{r.scores.letter ?? "-"}</Badge>
                <Button size="sm" variant="outline" onClick={() => navigate(`/students/${r.student.id}/report`)} className="rounded-full gap-1" data-testid={`view-report-${r.student.id}`}><FileText className="w-3.5 h-3.5" /> Rapor</Button>
              </div>
            </div>
          ))}
        </Card>
      )}

      <div id="pdf-pool" style={{ position: "fixed", left: "-10000px", top: 0 }}>
        {data.reports.map((r, i) => (
          <div key={r.student.id} style={i < data.reports.length - 1 ? { pageBreakAfter: "always" } : {}}>
            <ReportSheet id={`sheet-${r.student.id}`} d={{ ...meta, student: r.student, scores: r.scores, comment: r.comment }} />
          </div>
        ))}
      </div>
    </div>
  );
}
